import type { CSSProperties, ReactNode } from "react";
import { Count } from "./Pill";

/**
 * Menu lateral das páginas internas (Tarefas, Notificações, Configurações).
 *
 * É uma lista de filtros/seções dentro da página, não a navegação global: por
 * isso os itens são botões e o item ativo usa `aria-current="true"` em vez de
 * `page`. Em telas estreitas `components.css` transforma a coluna em uma faixa
 * rolável horizontal com alvos de 44 px (AGENTS §16).
 */
export interface SideNavItem {
  id: string;
  label: string;
  count?: number;
  icon?: ReactNode;
  /** Cor da barra do item ativo, quando o item tem identidade própria (funil). */
  tone?: string;
  disabled?: boolean;
  /** Motivo da indisponibilidade; vira `title` e nome acessível (§18). */
  disabledReason?: string;
}

export interface SideNavGroup {
  id: string;
  label?: string;
  /** Ação do cabeçalho do grupo, como "novo funil". */
  action?: ReactNode;
  items: SideNavItem[];
}

export interface SideNavProps {
  groups: SideNavGroup[];
  value: string;
  onChange: (id: string) => void;
  /** Nome acessível da navegação. */
  label: string;
  className?: string;
}

export function SideNav({ groups, value, onChange, label, className }: SideNavProps) {
  return (
    <nav aria-label={label} className={className ? `cx-side ${className}` : "cx-side"}>
      {groups.map((group, index) => (
        <div key={group.id}>
          {index > 0 && <hr />}
          {group.label && (
            <div className="cx-side-label">
              <span>{group.label}</span>
              {group.action}
            </div>
          )}
          {group.items.map((item) => {
            const active = item.id === value;
            return (
              <button
                key={item.id}
                type="button"
                className={active ? "is-active" : undefined}
                aria-current={active ? "true" : undefined}
                disabled={item.disabled}
                title={item.disabled ? item.disabledReason : undefined}
                style={item.tone ? ({ "--cx-side-tone": item.tone } as CSSProperties) : undefined}
                onClick={() => onChange(item.id)}
              >
                {item.icon && (
                  <span className="cx-side-ico" aria-hidden="true">
                    {item.icon}
                  </span>
                )}
                <span className="cx-side-text">{item.label}</span>
                {item.count !== undefined && <Count value={item.count} />}
                {item.disabled && item.disabledReason && (
                  <span className="cx-sr-only">{item.disabledReason}</span>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
